import React from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { TranslationKey } from "../../i18n/translations";
import styles from "./ServiceCard.module.css";

interface Service {
  id: string;
  titleKey: string;
  descriptionKey: string;
  priceEn: string;
  priceDe: string;
  features: string[];
  image?: string;
}

interface ServiceCardProps {
  service: Service;
  onInquire: () => void;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({ service, onInquire }) => {
  const { t, language } = useLanguage();

  const getPrice = (): string => {
    const price = language === "de" ? service.priceDe : service.priceEn;
    const startingAt = t("services.startingAt");
    return `${startingAt} ${price}`;
  };

  // Map service IDs to placeholder images (you can replace with actual image URLs)
  const getServiceImage = (id: string): string => {
    const images: Record<string, string> = {
      wedding: "#",
      portrait: "#",
      commercial: "#",
      event: "#",
      car: "#",
      family: "#"
    };
    return images[id] || images.portrait;
  };

  const imageUrl = service.image || getServiceImage(service.id);

  return (
    <div className={styles.serviceCard}>
      <div className={styles.cardImageWrapper}>
        <img
          src={imageUrl}
          alt={t(service.titleKey as TranslationKey)}
          className={styles.cardImage}
          loading="lazy"
        />
        <div className={styles.cardImageOverlay}>
          <span className={styles.cardCategory}>
            {t(service.titleKey as TranslationKey)}
          </span>
        </div>
      </div>

      <div className={styles.cardContent}>
        <h3 className={styles.serviceTitle}>
          {t(service.titleKey as TranslationKey)}
        </h3>

        <p className={styles.serviceDescription}>
          {t(service.descriptionKey as TranslationKey)}
        </p>

        <div className={styles.servicePrice}>
          {getPrice()}
        </div>

        <ul className={styles.serviceFeatures}>
          {service.features.map((featureKey, index) => (
            <li key={index} className={styles.serviceFeature}>
              {t(featureKey as TranslationKey)}
            </li>
          ))}
        </ul>

        <button
          className={styles.serviceButton}
          onClick={onInquire}
        >
          {t("services.inquireNow")}
        </button>
      </div>
    </div>
  );
};
